import {
  ColumnActionWithMenuV2,
  VerticalDivider,
} from '@labrav/react-components'
import { useFlagValue } from '@labrav/flags'
import { useAuth0 } from '@auth0/auth0-react'
import { useDispatch, useSelector } from 'react-redux'

import { InvitationStatus, Role } from '../../../utils/roles'
import { getRoleName } from '../../../utils/getRoleName'
import type { UserProfileState } from '../../../modules/userProfile/reducer'
import type { UserState } from '../../../utils/hooks/useGetUsers'
import { useStyles } from './InvitedUsers.styles'
import { AvatarName } from './AvatarName'
import {
  changeInvitedUserRole,
  getInvitedUserMenuOptions,
  isInvitedUserActionDisabled,
  partitionInvitedUsers,
  refreshSignedInUser,
  resendInvitedUserInvite,
  selectInvitedUserMenuOption,
} from './InvitedUsers.helpers'

type InvitedUsersRootState = {
  userProfile: UserProfileState
  AccountData: {
    user: {
      partnerData?: {
        partnerId?: string
      }
      isLoading: boolean
    }
  }
}

export const InvitedUsers = ({
  invitedUsers,
}: {
  invitedUsers: UserState[]
}) => {
  const { classes } = useStyles()
  const { loading: isFlagsLoading, value: auth0Enabled } = useFlagValue('auth0')

  const { userProfile } = useSelector(
    (state: InvitedUsersRootState) => state.userProfile
  )
  const isStaffUser =
    userProfile?.roles && userProfile?.roles[0] === Role.PARTNER_STAFF

  const isAdminUser =
    userProfile?.roles && userProfile?.roles[0] === Role.PARTNER_ADMIN
  const partnerId = useSelector(
    (state: InvitedUsersRootState) =>
      state.AccountData.user.partnerData?.partnerId
  )
  const dispatch = useDispatch()

  const { user } = useAuth0()

  const { partnerLoading } = useSelector((state: InvitedUsersRootState) => ({
    partnerLoading: state.AccountData.user.isLoading,
  }))
  void partnerLoading
  void isFlagsLoading

  const getUser = async () => {
    await refreshSignedInUser({
      dispatch,
      auth0Enabled,
      user: user as { partner_id?: string; partner_user_id?: string } | undefined,
    })
  }

  const handleResendInvite = async (
    selectedUser: UserState,
    handleClose?: () => void
  ) => {
    await resendInvitedUserInvite({
      dispatch,
      partnerId,
      auth0Enabled,
      selectedUser,
      handleClose,
    })
  }

  const changeRole = async (
    selectedUser: UserState,
    handleClose?: () => void,
    fromRole?: Role
  ) => {
    await changeInvitedUserRole({
      dispatch,
      partnerId,
      auth0Enabled,
      isAdminUser: !!isAdminUser,
      userProfileId: userProfile.id,
      selectedUser,
      handleClose,
      fromRole,
      refreshUser: () => {
        getUser()
      },
    })
  }

  const { ownerUsers, otherUsers } = partitionInvitedUsers(invitedUsers)

  return (
    <div className={classes.root}>
      {(ownerUsers || []).map((userItem, index) => {
        return <InvitedUserRow index={index} userItem={userItem} />
      })}
      {(otherUsers || []).map((userItem, index) => {
        return (
          <InvitedUserRow
            index={index}
            userItem={userItem}
            isStaffUser={!!isStaffUser}
            handleResendInvite={handleResendInvite}
            changeRole={changeRole}
          />
        )
      })}
    </div>
  )
}

const InvitedUserRow = ({
  userItem,
  index,
  isStaffUser,
  handleResendInvite,
  changeRole,
}: {
  userItem: UserState
  index: number
  isStaffUser?: boolean
  handleResendInvite?: (user: UserState, handleClose?: () => void) => void
  changeRole?: (
    user: UserState,
    handleClose?: () => void,
    fromRole?: Role
  ) => void
}) => {
  const { classes } = useStyles()

  return (
    <div className={classes.listRow} data-testid={'list-row-item'}>
      <div className={classes.emailSection}>
        <AvatarName index={index} user={userItem} isAdmin={false} />
        <span className={classes.email}>{userItem.email}</span>
        {userItem.invitationStatus === InvitationStatus.INVITED ? (
          <span className={classes.pending}>Pending acceptance</span>
        ) : (
          <></>
        )}
      </div>
      <div className={classes.actionPart}>
        <div>{getRoleName(userItem.roles[0])}</div>
        <VerticalDivider height="24px" />
        <ColumnActionWithMenuV2
          menuOptions={getInvitedUserMenuOptions(userItem)}
          handleSelectOption={(option, handleClose) =>
            selectInvitedUserMenuOption({
              option,
              userItem,
              handleClose,
              handleResendInvite,
              changeRole,
            })
          }
          key={'action'}
          zIndex={100000}
          disable={isInvitedUserActionDisabled(
            isStaffUser,
            getRoleName(userItem.roles[0])
          )}
          disabledActionTooltip={
            isStaffUser
              ? 'Your role does not permit you to take this action.'
              : ''
          }
        />
      </div>
    </div>
  )
}
